import {assertAndContinue} from 'Asserts';
import {getClientIdForProcessFlowchartEdge} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {
  ClientSchemaMapperConfig,
  isClientSchemaMapperConfigFullyDefined,
  DefinedClientSchemaMapperConfig,
} from 'processes/settings/graph-builder-flowchart-config/FlowchartConfigConstants';

import {
  ProcessFlowchartDesignSpace,
  ProcessGraphBuilderEdgeTemplate,
  ProcessTaskSchemaForGraphBuilderValidation,
  SchemaMapperConfig,
} from '../validation/ProcessGraphBuilderConstants';

function getInboundMapperConfigs(
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>,
  toTaskSchemaId: string
): Array<SchemaMapperConfig> {
  return schemaMapperConfigs
    .filter((config) => config.toTaskSchema?.id === toTaskSchemaId)
    .map((config) => ({
      clientId: config.clientId,
      fromTaskSchemaIds: config.fromTaskSchemas.map(({id}) => id),
      toTaskSchemaId,
    }));
}

function getAvailableEdges(
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>
): Array<ProcessGraphBuilderEdgeTemplate> {
  const availableEdges: Array<ProcessGraphBuilderEdgeTemplate> = [];
  const edgeIdsAdded: Set<string> = new Set();

  for (const mapperConfig of schemaMapperConfigs) {
    for (const fromSchema of mapperConfig.fromTaskSchemas) {
      const fromNodeId = fromSchema.id;
      const toNodeId = mapperConfig.toTaskSchema?.id;
      if (toNodeId == null) {
        continue;
      }
      const edgeId = getClientIdForProcessFlowchartEdge(fromNodeId, toNodeId);
      if (!edgeIdsAdded.has(edgeId)) {
        edgeIdsAdded.add(edgeId);
        availableEdges.push({
          fromNodeId,
          toNodeId,
          clientId: edgeId,
        });
      }
    }
  }
  return availableEdges;
}

export default function constructDesignSpace(
  availableTaskSchemas: Array<{id: string; name: string}>,
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>,
  flowchartSchema: ProcessTaskSchemaForGraphBuilderValidation
): ProcessFlowchartDesignSpace {
  if (
    assertAndContinue(
      availableTaskSchemas.find((s) => s.id === flowchartSchema.id) == null,
      "Root task schema should not be listed in 'available nodes'"
    )
  ) {
    availableTaskSchemas = availableTaskSchemas.filter((schema) => schema.id !== flowchartSchema.id);
  }

  const definedSchemaMapperConfigs = schemaMapperConfigs.filter(
    (schemaMapperConfig): schemaMapperConfig is DefinedClientSchemaMapperConfig =>
      isClientSchemaMapperConfigFullyDefined(schemaMapperConfig)
  );
  return {
    availableNodes: [
      ...availableTaskSchemas.map((taskSchema) => ({
        schemaId: taskSchema.id,
        clientId: taskSchema.id,
        name: taskSchema.name,
        inboundMapperConfigs: getInboundMapperConfigs(definedSchemaMapperConfigs, taskSchema.id),
      })),
      {
        schemaId: flowchartSchema.id,
        clientId: flowchartSchema.id,
        name: flowchartSchema.name,
        inboundMapperConfigs: getInboundMapperConfigs(definedSchemaMapperConfigs, flowchartSchema.id),
      },
    ],
    availableEdges: getAvailableEdges(definedSchemaMapperConfigs),
    flowchartSchema,
    flowchartConfig: {
      availableTaskSchemas,
      schemaMapperConfigs: definedSchemaMapperConfigs,
    },
  };
}
